import { getChainIdFromEnv } from "../../config/chains/getters";
import { LocalStorageKeyEnum } from "../../enums/localStorage";

export const getAccountKey = (account?: unknown) => {
  if (!account) return undefined;
  return String(account).toLowerCase();
};

/**
 * Storage key for the trading credential, namespaced by chain.
 *
 * The credential is an API key/secret from ONE backend plus a signing key
 * registered against ONE chain's EIP-712 domain, but `localStorage` is
 * partitioned by ORIGIN only — so in local dev several environments share a
 * store. Namespacing keeps each chain's credential intact instead of one
 * overwriting the other, and pairs with the fingerprint in
 * `credentialFingerprint.ts`, which is what actually rejects a foreign record.
 *
 * Uses the validated chain id rather than the raw env string, so an unset or
 * malformed REACT_APP_CHAIN_ID cannot silently produce its own storage bucket.
 */
export const getAuthInfoStorageKey = (): string =>
  `${LocalStorageKeyEnum.AUTH_INFO}:${getChainIdFromEnv()}`;

/**
 * Remove one account's session credential (API key, secret, signing key) from
 * sessionStorage, leaving other accounts' records alone. Used when the wallet
 * disconnects, so the session copy does not outlive the connection.
 *
 * The "remember me" copy in localStorage is deliberately kept (owner decision):
 * reconnecting must not force a new signature, and a brief connector blip that
 * reports no account must cost nothing.
 * Storage is untrusted input: anything unparseable is left untouched.
 */
export const clearStoredApiCredentials = (account: string) => {
  const accountKey = getAccountKey(account);
  if (!accountKey) return;
  const key = getAuthInfoStorageKey();
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;
    const remaining = Object.fromEntries(
      Object.entries(parsed).filter(
        ([storedKey, info]) =>
          storedKey.toLowerCase() !== accountKey &&
          getAccountKey((info as { account?: unknown })?.account) !== accountKey
      )
    );
    if (Object.keys(remaining).length !== Object.keys(parsed).length) {
      window.sessionStorage.setItem(key, JSON.stringify(remaining));
    }
  } catch {
    // unparseable or unavailable storage — nothing to clear
  }
};
